<template>
    <div class="space-y-6">
        <!-- Page Header -->
        <div class="text-center mb-8">
            <!-- Icon -->
            <div class="mx-auto w-16 h-16 bg-primary-100 dark:bg-primary-900/30 rounded-full flex items-center justify-center mb-4">
                <svg class="w-8 h-8 text-primary-600 dark:text-primary-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z" />
                </svg>
            </div>

            <h1 class="text-3xl font-bold text-secondary-900 dark:text-white mb-2">
                Reset Password
            </h1>
            <p class="text-secondary-600 dark:text-secondary-400">
                Enter your new password below
            </p>
        </div>

        <!-- Form -->
        <form @submit.prevent="submit" class="space-y-6">
            <!-- Email Address (readonly) -->
            <div>
                <label for="email" class="block text-sm font-medium text-secondary-700 dark:text-secondary-300 mb-2">
                    Email Address
                </label>
                <div class="relative">
                    <div class="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <svg class="h-5 w-5 text-secondary-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M16 12a4 4 0 10-8 0 4 4 0 008 0zm0 0v1.5a2.5 2.5 0 005 0V12a9 9 0 10-9 9m4.5-1.206a8.959 8.959 0 01-4.5 1.207" />
                        </svg>
                    </div>
                    <input
                        id="email"
                        v-model="form.email"
                        type="email"
                        readonly
                        class="input pl-10 bg-secondary-50 dark:bg-secondary-900/50 cursor-not-allowed"
                    />
                </div>
            </div>

            <!-- New Password -->
            <div>
                <label for="password" class="block text-sm font-medium text-secondary-700 dark:text-secondary-300 mb-2">
                    New Password
                </label>
                <div class="relative">
                    <div class="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <svg class="h-5 w-5 text-secondary-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                        </svg>
                    </div>
                    <input
                        id="password"
                        ref="passwordInput"
                        v-model="form.password"
                        :type="showPassword ? 'text' : 'password'"
                        autocomplete="new-password"
                        required
                        :class="[
                            'input pl-10 pr-10',
                            form.errors.password ? 'border-danger-300 dark:border-danger-600 focus:border-danger-500 focus:ring-danger-500' : ''
                        ]"
                        placeholder="Enter your new password"
                    />
                    <button
                        type="button"
                        @click="togglePasswordVisibility"
                        class="absolute inset-y-0 right-0 pr-3 flex items-center text-secondary-400 hover:text-secondary-600 dark:hover:text-secondary-300"
                        tabindex="-1"
                    >
                        <svg v-if="showPassword" class="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                        </svg>
                        <svg v-else class="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                        </svg>
                    </button>
                </div>
                <p v-if="form.errors.password" class="mt-2 text-sm text-danger-600 dark:text-danger-400">
                    {{ form.errors.password }}
                </p>
            </div>

            <!-- Password Strength Indicator -->
            <div v-if="form.password" class="space-y-2">
                <!-- Progress Bar -->
                <div class="h-2 bg-secondary-200 dark:bg-secondary-700 rounded-full overflow-hidden">
                    <div
                        :class="passwordStrength.color"
                        :style="{ width: passwordStrength.width }"
                        class="h-full transition-all duration-300"
                    ></div>
                </div>

                <!-- Strength Label -->
                <p class="text-sm font-medium" :class="passwordStrength.textColor">
                    Password strength: {{ passwordStrength.label }}
                </p>

                <!-- Requirements Checklist -->
                <ul class="space-y-1 text-sm">
                    <li :class="hasMinLength ? 'text-success-600 dark:text-success-400' : 'text-secondary-500 dark:text-secondary-500'">
                        <svg class="inline-block w-4 h-4 mr-1 -mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                            <path v-if="hasMinLength" fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd" />
                            <path v-else fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM7 9a1 1 0 000 2h6a1 1 0 100-2H7z" clip-rule="evenodd" />
                        </svg>
                        At least 8 characters
                    </li>
                    <li :class="hasUpperCase ? 'text-success-600 dark:text-success-400' : 'text-secondary-500 dark:text-secondary-500'">
                        <svg class="inline-block w-4 h-4 mr-1 -mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                            <path v-if="hasUpperCase" fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd" />
                            <path v-else fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM7 9a1 1 0 000 2h6a1 1 0 100-2H7z" clip-rule="evenodd" />
                        </svg>
                        One uppercase letter
                    </li>
                    <li :class="hasLowerCase ? 'text-success-600 dark:text-success-400' : 'text-secondary-500 dark:text-secondary-500'">
                        <svg class="inline-block w-4 h-4 mr-1 -mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                            <path v-if="hasLowerCase" fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd" />
                            <path v-else fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM7 9a1 1 0 000 2h6a1 1 0 100-2H7z" clip-rule="evenodd" />
                        </svg>
                        One lowercase letter
                    </li>
                    <li :class="hasNumber ? 'text-success-600 dark:text-success-400' : 'text-secondary-500 dark:text-secondary-500'">
                        <svg class="inline-block w-4 h-4 mr-1 -mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                            <path v-if="hasNumber" fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd" />
                            <path v-else fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM7 9a1 1 0 000 2h6a1 1 0 100-2H7z" clip-rule="evenodd" />
                        </svg>
                        One number
                    </li>
                </ul>
            </div>

            <!-- Confirm Password -->
            <div>
                <label for="password_confirmation" class="block text-sm font-medium text-secondary-700 dark:text-secondary-300 mb-2">
                    Confirm New Password
                </label>
                <div class="relative">
                    <div class="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <svg class="h-5 w-5 text-secondary-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                    </div>
                    <input
                        id="password_confirmation"
                        v-model="form.password_confirmation"
                        :type="showPassword ? 'text' : 'password'"
                        autocomplete="new-password"
                        required
                        :class="[
                            'input pl-10',
                            form.errors.password_confirmation ? 'border-danger-300 dark:border-danger-600 focus:border-danger-500 focus:ring-danger-500' : '',
                            passwordsMatch && form.password_confirmation ? 'border-success-300 dark:border-success-600' : ''
                        ]"
                        placeholder="Confirm your new password"
                    />
                    <!-- Match Indicator -->
                    <div v-if="form.password_confirmation" class="absolute inset-y-0 right-0 pr-3 flex items-center">
                        <svg v-if="passwordsMatch" class="h-5 w-5 text-success-500" fill="currentColor" viewBox="0 0 20 20">
                            <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd" />
                        </svg>
                        <svg v-else class="h-5 w-5 text-danger-500" fill="currentColor" viewBox="0 0 20 20">
                            <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clip-rule="evenodd" />
                        </svg>
                    </div>
                </div>
                <p v-if="form.errors.password_confirmation" class="mt-2 text-sm text-danger-600 dark:text-danger-400">
                    {{ form.errors.password_confirmation }}
                </p>
                <p v-else-if="form.password_confirmation && !passwordsMatch" class="mt-2 text-sm text-danger-600 dark:text-danger-400">
                    Passwords do not match
                </p>
            </div>

            <!-- Submit Button -->
            <button
                type="submit"
                :disabled="form.processing || !isFormValid"
                class="w-full btn btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
            >
                <svg
                    v-if="form.processing"
                    class="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
                    fill="none"
                    viewBox="0 0 24 24"
                >
                    <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                    <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                <span v-if="form.processing">Resetting Password...</span>
                <span v-else>Reset Password</span>
            </button>
        </form>

        <!-- Divider -->
        <div class="relative my-6">
            <div class="absolute inset-0 flex items-center">
                <div class="w-full border-t border-secondary-200 dark:border-secondary-700"></div>
            </div>
            <div class="relative flex justify-center text-sm">
                <span class="px-2 bg-white dark:bg-secondary-800 text-secondary-500 dark:text-secondary-400">
                    or
                </span>
            </div>
        </div>

        <!-- Back to Login -->
        <Link
            href="/login"
            class="block w-full text-center px-4 py-2.5 border border-secondary-300 dark:border-secondary-600 rounded-lg text-secondary-700 dark:text-secondary-300 hover:bg-secondary-50 dark:hover:bg-secondary-800 transition-colors font-medium"
        >
            <svg class="inline-block w-4 h-4 mr-2 -mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 19l-7-7m0 0l7-7m-7 7h18" />
            </svg>
            Back to Login
        </Link>
    </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue';
import { Link, useForm, router } from '@inertiajs/vue3';

// Props
const props = defineProps({
    token: {
        type: String,
        required: true,
    },
    email: {
        type: String,
        required: true,
    },
});

// Form
const form = useForm({
    token: props.token,
    email: props.email,
    password: '',
    password_confirmation: '',
});

// Refs
const passwordInput = ref(null);
const showPassword = ref(false);

// Toggle password visibility
const togglePasswordVisibility = () => {
    showPassword.value = !showPassword.value;
};

// Password validation
const hasMinLength = computed(() => form.password.length >= 8);
const hasUpperCase = computed(() => /[A-Z]/.test(form.password));
const hasLowerCase = computed(() => /[a-z]/.test(form.password));
const hasNumber = computed(() => /[0-9]/.test(form.password));

// Password strength
const passwordStrength = computed(() => {
    let strength = 0;
    if (hasMinLength.value) strength++;
    if (hasUpperCase.value) strength++;
    if (hasLowerCase.value) strength++;
    if (hasNumber.value) strength++;

    if (strength <= 2) {
        return {
            width: '33%',
            label: 'Weak',
            color: 'bg-danger-500',
            textColor: 'text-danger-600 dark:text-danger-400'
        };
    } else if (strength === 3) {
        return {
            width: '66%',
            label: 'Fair',
            color: 'bg-warning-500',
            textColor: 'text-warning-600 dark:text-warning-400'
        };
    } else {
        return {
            width: '100%',
            label: 'Strong',
            color: 'bg-success-500',
            textColor: 'text-success-600 dark:text-success-400'
        };
    }
});

// Passwords match
const passwordsMatch = computed(() => {
    return form.password === form.password_confirmation && form.password_confirmation.length > 0;
});

// Form validation
const isFormValid = computed(() => {
    return (
        form.email &&
        hasMinLength.value &&
        hasUpperCase.value &&
        hasLowerCase.value &&
        hasNumber.value &&
        passwordsMatch.value
    );
});

// Submit form
const submit = () => {
    if (!isFormValid.value) {
        return;
    }

    form.post('/api/auth/reset-password', {
        preserveScroll: true,
        onSuccess: () => {
            router.visit('/login');
        },
        onError: (errors) => {
            if (passwordInput.value) {
                passwordInput.value.focus();
            }
        },
    });
};

// Auto-focus on mount
onMounted(() => {
    if (passwordInput.value) {
        passwordInput.value.focus();
    }
});
</script>

<style scoped>
/* Smooth transitions */
* {
    transition-property: color, background-color, border-color, opacity;
    transition-timing-function: cubic-bezier(0.4, 0, 0.2, 1);
    transition-duration: 150ms;
}
</style>
